import Fuse, { type FuseOptionKey } from 'fuse.js';
import { useEffect, useMemo } from 'react';
import { useDebounceValue } from './useDebounce';

/**
 * Custom hook for fuzzy searching through an array of data.
 *
 * This hook uses Fuse.js to perform fuzzy searching on the provided data array.
 * It memoizes both the Fuse instance and search results to optimize performance.
 *
 * @template DataType - The type of items in the data array
 *
 * @param {Array<DataType> | null | undefined} data - The array of items to search through
 * @param {string} search - The search query string
 * @param {Array<FuseOptionKey<DataType>>} fields - The fields of each item to include in the search
 *
 * @returns {Array<DataType>} An array of items that match the search query.
 *   Returns the original array if the search string is empty.
 *   Returns an empty array if the input data is null or undefined.
 */
export function useFuzzySearch<DataType>(
  data: Array<DataType> | null | undefined,
  search: string,
  fields: Array<FuseOptionKey<DataType>>,
  debounceTime = 300, // Optional debounce time in milliseconds
): Array<DataType> {
  const [searchValueDebounced, setSearchValueDebounced] = useDebounceValue(search, debounceTime);

  const fuse = useMemo(
    () =>
      new Fuse(data || [], {
        keys: fields,
        threshold: 0.3,
        useExtendedSearch: true,
      }),
    [data, fields],
  );

  useEffect(() => {
    setSearchValueDebounced(search);
  }, [search, setSearchValueDebounced]);

  const result = useMemo(() => {
    if (searchValueDebounced === '') {
      return data;
    }
    const searchResult = fuse.search(searchValueDebounced);
    return searchResult.map((r) => r.item);
  }, [fuse, searchValueDebounced, data]);

  return result || [];
}
